import { useState, useEffect, useCallback } from 'react';
import { evaluatePromoVerifyResponse } from './promoValidator';

/**
 * Hook agnóstico de checkout — gere o estado dos 3 inputs de desconto (Código
 * Promocional, Vales, Créditos) sem depender de nenhum endpoint específico.
 *
 * As 2 callsites (MY checkout + CRM Nova Encomenda) injectam:
 *
 *   - `loadBalances()`: Promise<{vouchers,credits}> — saldos do cliente alvo.
 *   - `verifyPromo(code, { appliedVales, appliedCreditos })`: Promise<spResult>
 *     — chama o endpoint de verify com o código a testar e devolve a primeira
 *     linha do array `data` (formato comum do SP).
 *   - `prerequisitesValid()`: () => boolean — true quando há pré-requisitos
 *     (transporte + morada) preenchidos para o SP correr.
 *
 * Os inputs `applyVales` / `applyCreditos` apenas guardam o valor pedido — o
 * cap é sempre feito pelo SP via verify, e o valor efectivamente descontado
 * vem em `descontoVale` / `descontoCreditos` do extractor.
 */
export default function useDiscountsLogic({ loadBalances, verifyPromo, prerequisitesValid }) {
  const [balances, setBalances] = useState({ vouchers: 0, credits: 0 });

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [promoSuccess, setPromoSuccess] = useState('');

  const [valesValue, setValesValue] = useState('');
  const [appliedVales, setAppliedVales] = useState(0);
  const [creditosValue, setCreditosValue] = useState('');
  const [appliedCreditos, setAppliedCreditos] = useState(0);

  useEffect(() => {
    if (typeof loadBalances !== 'function') return;
    loadBalances()
      .then((b) => setBalances({
        vouchers: Number(b?.vouchers || 0),
        credits: Number(b?.credits || 0),
      }))
      .catch(() => {});
  }, [loadBalances]);

  const applyPromo = useCallback(async () => {
    const code = promoCode.trim();
    if (!code) { setPromoError('Código promocional por preencher!'); return; }
    if (typeof prerequisitesValid === 'function' && !prerequisitesValid()) {
      setPromoError('Preencha primeiro a forma de envio e a morada.');
      return;
    }
    setPromoLoading(true); setPromoError(''); setPromoSuccess('');
    try {
      const spResult = await verifyPromo(code, { appliedVales, appliedCreditos });
      const r = evaluatePromoVerifyResponse(spResult, code);
      if (!r.ok) {
        setPromoError(r.error);
        setAppliedPromo('');
      } else {
        setAppliedPromo(code);
        setPromoSuccess('Código promocional inserido com sucesso');
        setPromoCode('');
      }
    } catch (e) {
      setPromoError('Erro ao validar código.');
      if (process.env.NODE_ENV !== 'production') console.warn('applyPromo failed', e);
    } finally { setPromoLoading(false); }
  }, [promoCode, prerequisitesValid, verifyPromo, appliedVales, appliedCreditos]);

  const removePromo = useCallback(() => {
    setAppliedPromo(''); setPromoError(''); setPromoSuccess('');
  }, []);

  const applyVales = useCallback(() => {
    const v = parseFloat(String(valesValue).replace(',', '.'));
    if (Number.isNaN(v) || v <= 0) { setValesValue(''); return; }
    setAppliedVales(v);
    setValesValue('');
  }, [valesValue]);
  const removeVales = useCallback(() => setAppliedVales(0), []);

  const applyCreditos = useCallback(() => {
    const v = parseFloat(String(creditosValue).replace(',', '.'));
    if (Number.isNaN(v) || v <= 0) { setCreditosValue(''); return; }
    setAppliedCreditos(v);
    setCreditosValue('');
  }, [creditosValue]);
  const removeCreditos = useCallback(() => setAppliedCreditos(0), []);

  return {
    balances,
    promoCode, setPromoCode,
    appliedPromo, applyPromo, removePromo,
    promoLoading, promoError, promoSuccess, setPromoError,
    valesValue, setValesValue, appliedVales, applyVales, removeVales,
    creditosValue, setCreditosValue, appliedCreditos, applyCreditos, removeCreditos,
  };
}
